import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Which tenant's branding the login screen should wear.
 *
 * Resolved without asking the user: `?org=` for a link sent by an
 * administrator, the subdomain for the usual cloud setup, and a build-time
 * value for an On-Premise install that serves exactly one organisation.
 */
export function tenantSlugFromLocation(): string | null {
  const fromQuery = new URLSearchParams(window.location.search).get('org');
  if (fromQuery) return fromQuery;

  const configured = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (configured) return configured;

  const [subdomain, ...rest] = window.location.hostname.split('.');
  // `app.4zen.crm` and bare hosts carry no tenant; `acme.4zen.crm` does.
  if (rest.length < 2 || ['app', 'www', 'localhost'].includes(subdomain)) return null;
  return subdomain;
}

/** Branding readable before sign-in. Absent tenant simply yields defaults. */
export function usePublicBranding(slug: string | null) {
  return useQuery({
    queryKey: ['public-branding', slug],
    queryFn: () => api.public.branding(slug as string),
    enabled: Boolean(slug),
    staleTime: 60 * 60_000,
    retry: false,
  });
}
